import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { positionAsync } from '../../positions/positionPicker';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

const useBenefitPackageApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/positions?pageSize=1000').then((res) => {
      setPositions(res.data?.data?.data || []);
    }).catch(() => {});
    api.get('/v1/departments?pageSize=1000').then((res) => {
      setDepartments(res.data?.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/benefit-packages?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      // New packages start with empty items — IK adds them on the details page
      await api.post('/v1/benefit-packages', { ...formData, items: [] });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      // Re-send current items (form doesn't edit them — that lives on details page)
      const current = await api.get(`/v1/benefit-packages/${id}`);
      const items = (current.data?.data?.items || []).map((it) => ({
        benefitId: it.benefitId,
        isMandatory: it.isMandatory,
        minimumLimit: it.minimumLimit,
        overrideCompanyCost: it.overrideCompanyCost,
      }));
      await api.put(`/v1/benefit-packages/${id}`, { ...formData, items });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/benefit-packages/${ids[0]}`);
      } else {
        await api.post('/v1/benefit-packages/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const positionDropdownOptions = useMemo(
    () => positions.map((p) => ({
      id: p.id,
      name: `${p.code || '-'}${p.titleName ? ' - ' + p.titleName : ''}`,
    })),
    [positions]
  );

  const departmentDropdownOptions = useMemo(
    () => departments.map((d) => ({ id: d.id, name: d.name })),
    [departments]
  );

  const labelOf = (list) => (val) => list.find((o) => o.id === val)?.name || val;

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('benefitPackage.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'targetPositionId', label: t('benefitPackage.targetPosition'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: positionDropdownOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: labelOf(positionDropdownOptions),
        },
        customFilterListOptions: { render: labelOf(positionDropdownOptions) },
        customBodyRenderLite: (dataIndex) => data[dataIndex]?.targetPositionName || '-',
      },
    },
    {
      name: 'targetDepartmentId', label: t('benefitPackage.targetDepartment'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: departmentDropdownOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: labelOf(departmentDropdownOptions),
        },
        customFilterListOptions: { render: labelOf(departmentDropdownOptions) },
        customBodyRenderLite: (dataIndex) => data[dataIndex]?.targetDepartmentName || '-',
      },
    },
    numericFilterColumn('itemCount', t('benefitPackage.itemCount'), { decimals: 0, sort: false }),
    numericFilterColumn('assignedEmployeeCount', t('benefitPackage.assignedEmployeeCount'), { decimals: 0, sort: false }),
    numericFilterColumn('totalMonthlyCost', t('benefitPackage.totalMonthlyCost'), {
      decimals: 2,
      suffix: '₺',
      customBodyRender: (v) => `${Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
    }),
    {
      name: 'cafeteriaAnnualBudget', label: t('benefitPackage.cafeteriaAnnualBudget'),
      options: {
        filter: false, sort: false, display: false,
        customBodyRender: (v) => v != null ? `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺` : '-',
      },
    },
    {
      ...dateFilterColumn('effectiveStartDate', t('benefitPackage.effectiveStartDate'), lang),
      options: {
        ...dateFilterColumn('effectiveStartDate', t('benefitPackage.effectiveStartDate'), lang).options,
        customBodyRender: (v) => v ? new Date(v).toLocaleDateString('tr-TR') : '-',
      },
    },
  ], [t, lang, data, positionDropdownOptions, departmentDropdownOptions]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('benefitPackage.name'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('benefitPackage.description'), type: 'textarea', grid: 12 },
    {
      // Ortak zengin pozisyon seçici (ünvan · departman · oturan kişi · kod). Statik
      // 1000'lik liste büyük kiracıda pozisyonların bir kısmını hiç sunmuyordu.
      name: 'targetPositionId', label: t('benefitPackage.targetPosition'), type: 'asyncSelect', grid: 6,
      ...positionAsync,
      searchPlaceholder: t('position.pickerSearchPlaceholder'),
    },
    {
      name: 'targetDepartmentId', label: t('benefitPackage.targetDepartment'), type: 'select', grid: 6,
      options: departments, idKey: 'id', labelKey: 'name',
    },
    { name: 'effectiveStartDate', label: t('benefitPackage.effectiveStartDate'), type: 'date', grid: 6, required: true },
    { name: 'effectiveEndDate', label: t('benefitPackage.effectiveEndDate'), type: 'date', grid: 6 },
    { name: 'cafeteriaAnnualBudget', label: t('benefitPackage.cafeteriaAnnualBudget'), type: 'number', grid: 12 },
  ], [t, departments]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useBenefitPackageApi;
